// src/pages/AuctionDetailPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './AuctionDetailPage.css';
import TopBar from '../components/TopBar';
import { useAuth } from '../context/AuthContext';
import useCustomCategoryLabel from '../hooks/useCustomCategoryLabel';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ensureFantasyDataSynced, getPlayersFromFirestore, getLineupStatusByPlayerId } from '../firebase/playerService';
import {
  getAuctionById,
  getAuctionPurchases,
  subscribeToAuctionPurchases,
  savePurchase,
  deletePurchase,
  updateAuction,
  normalizeRosterLimits,
  normalizeRosterMinLimits,
  DEFAULT_ROSTER_LIMITS,
  getAuctionRolesMode,
  getMarketPmaByPlayerIdV2,
  backfillPmaStats,
  getAuctionPlayerCategories,
  saveAuctionPlayerCategory,
  normalizePlayerCategory,
  getAuctionPlayerNotes,
  saveAuctionPlayerNote,
  getAuctionPlayerBackupGoalkeepers,
  getAuctionPlayerTiers,
  saveAuctionPlayerTier,
  normalizePlayerTier,
  getAuctionPlayerMaxSpend,
  saveAuctionPlayerMaxSpend,
  normalizePlayerMaxSpend,
  normalizeRoleOverridesInput,
  savePurchaseValuation,
  importStrategyIntoAuction,
  clearAuctionStrategy,
  isListoneAuction,
  deletePurchaseWithRecovery,
  updateAuctionLiveSelection,
  subscribeToAuctionLiveSelection,
} from '../firebase/auctionService';
import { ensureInjuriesSynced, applyInjuryTagsToAuction } from '../firebase/injuriesService';
import { ArrowLeft, Lock, Eye, Trash2, ChevronDown, ChevronUp, Download, Eraser, Upload } from 'lucide-react';
import { Metric } from '../components/auction/auctionCommon';
import Tooltip from '../components/Tooltip';
import AuctionMarketTab from '../components/auction/AuctionMarketTab';
import AuctionTeamsTab from '../components/auction/AuctionTeamsTab';
import AuctionUnavailableTab from '../components/auction/AuctionUnavailableTab';
import AuctionFormationTab from '../components/auction/AuctionFormationTab';
import AuctionMantraManageTab from '../components/auction/AuctionMantraManageTab';
import AuctionListoneSquadraTab from '../components/auction/AuctionListoneSquadraTab';
import AuctionListoneLegaTab from '../components/auction/AuctionListoneLegaTab';
import { useLeagueRostersPersistence, writeLeagueRosters } from '../utils/leagueRostersStore';
import ListoneRosterImport from '../components/auction/ListoneRosterImport';
import StrategyManage from '../components/auction/StrategyManage';
import { DEFAULT_MANTRA_ROLE_CONFIGURATION } from '../components/auction/MantraRoleConfiguration';
import { FANTAPP_ROLE_OVERRIDES_CONFIGURATION } from '../components/auction/FantAppRoleConfiguration';
import {
  buildRoleOverrideMap,
  applyRoleOverrideToPlayer,
  applyRoleOverrideToPurchase,
  applyLivePlayerDataToPurchase,
  buildTeamEconomy,
  buildRosterByTeam,
  buildMyRosterPurchases,
  buildListoneEconomy,
} from '../components/auction/auctionRosterUtils';

function openPlayerDetailNewTab(playerId) {
  window.open(`${window.location.origin}/players/${playerId}`, '_blank', 'noopener,noreferrer');
}

function updatePurchaseListAfterSave(prevPurchases, savedPurchase) {
  const next = [...prevPurchases];
  const idx = next.findIndex((p) => String(p.playerId) === String(savedPurchase.playerId));

  if (idx >= 0) {
    next[idx] = {
      ...next[idx],
      ...savedPurchase,
      deleted: false,
    };
  } else {
    next.unshift(savedPurchase);
  }

  return next;
}

function updatePurchaseListAfterDelete(prevPurchases, playerId) {
  return prevPurchases.map((p) =>
    String(p.playerId) === String(playerId)
      ? { ...p, deleted: true }
      : p
  );
}

function roleOverridesEqual(a = {}, b = {}) {
  const sortEntries = (obj) => Object.entries(obj || {}).sort(([k1], [k2]) => k1.localeCompare(k2));
  return JSON.stringify(sortEntries(a)) === JSON.stringify(sortEntries(b));
}

export default function AuctionDetailPage() {

  const { user } = useAuth();
  const navigate = useNavigate();
  const { auctionId } = useParams();
  const [searchParams] = useSearchParams();
  const ownerUid = searchParams.get('ownerUid') || user?.uid;
  const isReadOnly = ownerUid !== user?.uid;

  // Nome dell'etichetta "Personalizzata": account-level (users/{uid}), letta e
  // rinominabile solo per l'utente loggato — le categorie in asta esistono
  // comunque solo per il proprietario (vedi caricamento playerCategories più
  // sotto, saltato quando isReadOnly).
  const [customCategoryLabel, renameCustomCategoryLabel] = useCustomCategoryLabel(user?.uid);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('market');

  const [auction, setAuction] = useState(null);
  const [players, setPlayers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [marketPmaByPlayerId, setMarketPmaByPlayerId] = useState({});
  const [marketJump, setMarketJump] = useState(null);
  // Selezione corrente nel tab Mercato (giocatore + crediti impostati), per
  // mostrarla nel riquadro "in evidenza" della visualizzazione compatta del
  // tab Squadre — anche in un'ALTRA scheda/dispositivo (stessa asta aperta
  // due volte, o un pubblico che la segue). Sincronizzata su Firestore
  // (campo `liveSelection` sul documento asta): questo stato viene popolato
  // dalla sottoscrizione live, non direttamente dal tab Mercato.
  const [marketSelection, setMarketSelection] = useState(null);
  const marketSelectionWriteTimerRef = useRef(null);

  // Solo il proprietario trasmette la propria selezione (un pubblico che
  // guarda in sola lettura non deve sovrascrivere ciò che l'asta mostra a
  // tutti). Debounce: i crediti cambiano a ogni tasto premuto, non serve
  // scrivere su Firestore così spesso.
  const handleMarketSelectionChange = useCallback((selection) => {
    if (isReadOnly || !user?.uid || !auctionId) return;
    if (marketSelectionWriteTimerRef.current) clearTimeout(marketSelectionWriteTimerRef.current);
    marketSelectionWriteTimerRef.current = setTimeout(() => {
      updateAuctionLiveSelection(user.uid, auctionId, selection).catch(() => {});
    }, 350);
  }, [isReadOnly, user?.uid, auctionId]);

  useEffect(() => {
    if (!ownerUid || !auctionId) return undefined;
    const unsubscribe = subscribeToAuctionLiveSelection(ownerUid, auctionId, setMarketSelection);
    return () => {
      unsubscribe();
      if (marketSelectionWriteTimerRef.current) clearTimeout(marketSelectionWriteTimerRef.current);
    };
  }, [ownerUid, auctionId]);
  const [playerCategoriesById, setPlayerCategoriesById] = useState({});
  const [savingPlayerCategoryId, setSavingPlayerCategoryId] = useState(null);
  const [playerTiersById, setPlayerTiersById] = useState({});
  const [savingPlayerTierId, setSavingPlayerTierId] = useState(null);
  const [playerMaxSpendById, setPlayerMaxSpendById] = useState({});
  const [savingPlayerMaxSpendId, setSavingPlayerMaxSpendId] = useState(null);
  const [formationTeamName, setFormationTeamName] = useState('');
  const [playerNotesById, setPlayerNotesById] = useState({});
  const [playerBackupGoalkeepersById, setPlayerBackupGoalkeepersById] = useState({});
  const [playProbabilityById, setPlayProbabilityById] = useState({});
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [heroCollapsed, setHeroCollapsed] = useState(true);
  const [showImportStrategy, setShowImportStrategy] = useState(false);
  const [showResetStrategy, setShowResetStrategy] = useState(false);
  const [resettingStrategy, setResettingStrategy] = useState(false);

  const rolesMode = getAuctionRolesMode(auction);
  const isListone = isListoneAuction(auction);
  // Rose della lega importate nel tab Lega: salvate su Firestore (vedi leagueRostersStore).
  useLeagueRostersPersistence(isListone ? ownerUid : null, auctionId, !isReadOnly);
  const roleOverrideMap = useMemo(() => buildRoleOverrideMap(auction), [auction]);

  // Asta a listone: le tab valide sono Squadra / Formazione / Lega (+ Analisi
  // solo se ruoli Mantra). `tab` parte da 'market' (default aste normali):
  // appena sappiamo che è un listone lo spostiamo su 'squadra'.
  useEffect(() => {
    if (isListone) {
      const valid = rolesMode === 'mantra'
        ? ['squadra', 'formazione', 'analisi', 'lega']
        : ['squadra', 'formazione', 'lega'];
      setTab((cur) => (valid.includes(cur) ? cur : 'squadra'));
    } else {
      setTab((cur) => (['squadra', 'analisi', 'lega'].includes(cur) ? 'market' : cur));
    }
  }, [isListone, rolesMode]);

  const rosterLimits = useMemo(() => {
    if (!auction) return normalizeRosterLimits(DEFAULT_ROSTER_LIMITS);
    return normalizeRosterLimits(auction.rosterLimits || DEFAULT_ROSTER_LIMITS);
  }, [auction]);

  const rosterMinLimits = useMemo(
    () => normalizeRosterMinLimits(auction?.rosterMinLimits || {}, rosterLimits),
    [auction, rosterLimits]
  );

  const teamNames = auction?.teamNames || [];
  const myTeamName = auction?.myTeamName || teamNames[auction?.myTeamIndex || 0] || teamNames[0] || '';

  const effectivePlayers = useMemo(
    () => players.map((player) => applyRoleOverrideToPlayer(player, roleOverrideMap)),
    [players, roleOverrideMap]
  );

  const playersById = useMemo(() => {
    const map = {};
    players.forEach((player) => { map[String(player.id)] = player; });
    return map;
  }, [players]);

  const effectivePurchases = useMemo(
    () => purchases
      .map((purchase) => applyRoleOverrideToPurchase(purchase, roleOverrideMap))
      .map((purchase) => applyLivePlayerDataToPurchase(purchase, playersById)),
    [purchases, roleOverrideMap, playersById]
  );

  const teamEconomy = useMemo(
    () =>
      buildTeamEconomy({
        teamNames,
        purchases: effectivePurchases,
        auction,
        rosterLimits,
        rosterMinLimits,
        rolesMode,
      }),
    [teamNames, effectivePurchases, auction, rosterLimits, rosterMinLimits, rolesMode]
  );

  const rosterByTeam = useMemo(
    () =>
      buildRosterByTeam({
        teamNames,
        purchases: effectivePurchases,
      }),
    [teamNames, effectivePurchases]
  );

  const myRosterPurchases = useMemo(
    () => buildMyRosterPurchases({ rosterByTeam, myTeamName }),
    [rosterByTeam, myTeamName]
  );

  const listoneEconomy = useMemo(
    () => buildListoneEconomy({ auction, purchases: effectivePurchases, rosterLimits, rolesMode }),
    [auction, effectivePurchases, rosterLimits, rolesMode]
  );


  useEffect(() => {
    if (!formationTeamName && myTeamName) {
      setFormationTeamName(myTeamName);
    }
  }, [formationTeamName, myTeamName]);

  const loadAll = useCallback(async () => {
    if (!user?.uid || !auctionId) return;

    setLoading(true);
    try {
      const auctionData = await getAuctionById(ownerUid, auctionId);
      if (!auctionData) {
        setAuction(null);
        setPlayers([]);
        setPurchases([]);
        setMarketPmaByPlayerId({});
        setPlayerCategoriesById({});
        setPlayerTiersById({});
        setPlayerMaxSpendById({});
        setPlayerNotesById({});
        setPlayProbabilityById({});

        return;
      }

      let currentPlayers = await getPlayersFromFirestore().catch(() => []);
      if (!currentPlayers.length) {
        await ensureFantasyDataSynced();
        currentPlayers = await getPlayersFromFirestore().catch(() => []);
      }

      // I ruoli (Mantra da MantraRoleConfiguration.js, Classic FantApp da
      // FantAppRoleConfiguration.js) vivono in questi file ma vengono
      // congelati dentro l'asta al momento della creazione
      // (auction.roleOverrides). Ad ogni ingresso li riallineiamo alla
      // versione corrente del file, così non serve creare una nuova asta
      // per vedere le modifiche.
      let effectiveAuctionData = auctionData;
      const auctionRolesMode = getAuctionRolesMode(auctionData);
      const isFantAppClassic = auctionRolesMode === 'classic'
        && String(auctionData.playersSource || '').toLowerCase() === 'fantapp';

      if (auctionRolesMode === 'mantra' || isFantAppClassic) {
        const freshRoleOverrides = normalizeRoleOverridesInput(
          auctionRolesMode === 'mantra'
            ? DEFAULT_MANTRA_ROLE_CONFIGURATION
            : FANTAPP_ROLE_OVERRIDES_CONFIGURATION
        );
        const storedRoleOverrides = normalizeRoleOverridesInput(auctionData.roleOverrides || {});

        if (!roleOverridesEqual(freshRoleOverrides, storedRoleOverrides)) {
          effectiveAuctionData = { ...auctionData, roleOverrides: freshRoleOverrides };
          if (!isReadOnly) {
            updateAuction(user.uid, auctionId, { roleOverrides: freshRoleOverrides }).catch(() => {});
          }
        }
      }

      const localRoleMap = buildRoleOverrideMap(effectiveAuctionData);
      const [purchaseData, pmaData, categoryData, notesData, tierData, maxSpendData, backupGoalkeeperData] = await Promise.all([
        getAuctionPurchases(ownerUid, auctionId),
        getMarketPmaByPlayerIdV2({ ...auctionData, id: auctionId }).catch(() => ({})),
        isReadOnly ? Promise.resolve({}) : getAuctionPlayerCategories(user.uid, auctionId).catch(() => ({})),
        isReadOnly ? Promise.resolve({}) : getAuctionPlayerNotes(user.uid, auctionId).catch(() => ({})),
        isReadOnly ? Promise.resolve({}) : getAuctionPlayerTiers(user.uid, auctionId).catch(() => ({})),
        isReadOnly ? Promise.resolve({}) : getAuctionPlayerMaxSpend(user.uid, auctionId).catch(() => ({})),
        isReadOnly ? Promise.resolve({}) : getAuctionPlayerBackupGoalkeepers(user.uid, auctionId).catch(() => ({})),
      ]);

      const normalizedPlayers = currentPlayers.map((player) =>
        applyRoleOverrideToPlayer(player, localRoleMap)
      );

      const normalizedPurchases = purchaseData.map((purchase) =>
        applyRoleOverrideToPurchase(purchase, localRoleMap)
      );

      const normalizedAuctionData = {
        ...effectiveAuctionData,
        id: auctionId,
        rosterLimits: normalizeRosterLimits(auctionData.rosterLimits || DEFAULT_ROSTER_LIMITS),
      };

      setAuction(normalizedAuctionData);
      setPlayers(normalizedPlayers);
      setPurchases(normalizedPurchases);
      setMarketPmaByPlayerId(pmaData || {});
      setPlayerCategoriesById(categoryData || {});
      setPlayerTiersById(tierData || {});
      setPlayerMaxSpendById(maxSpendData || {});
      setPlayerNotesById(notesData || {});
      setPlayerBackupGoalkeepersById(backupGoalkeeperData || {});

      // Fire-and-forget: la probabilità di impiego dipende da un endpoint esterno
      // non sotto il nostro controllo (a volte lento/instabile) e non deve mai
      // bloccare l'ingresso in asta. Popola il tab quando (e se) arriva.
      getLineupStatusByPlayerId(currentPlayers)
        .then((lineupStatusData) => setPlayProbabilityById(lineupStatusData?.byId || {}))
        .catch(() => {});

      // Backfill fire-and-forget: popola pmaStats per gli acquisti esistenti (solo owner)
      if (!isReadOnly) {
        backfillPmaStats(user.uid, auctionId, normalizedPurchases, normalizedAuctionData).catch(() => {});

        // Fire-and-forget: sincronizza gli infortunati Serie A (al massimo ogni ~11h,
        // condiviso tra tutti i client) e applica categoria "injury" + nota ai
        // giocatori di questa asta che risultano infortunati.
        ensureInjuriesSynced()
          .then(() => applyInjuryTagsToAuction({
            uid: user.uid,
            auctionId,
            players: normalizedPlayers,
            existingCategories: categoryData || {},
            existingNotes: notesData || {},
          }))
          .then(({ updatedCategories, updatedNotes }) => {
            if (updatedCategories && Object.keys(updatedCategories).length) {
              setPlayerCategoriesById((prev) => ({ ...prev, ...updatedCategories }));
            }
            if (updatedNotes && Object.keys(updatedNotes).length) {
              setPlayerNotesById((prev) => ({ ...prev, ...updatedNotes }));
            }
          })
          .catch(() => {});
      }

    } finally {
      setLoading(false);
    }
  }, [auctionId, user?.uid, ownerUid, isReadOnly]);

  useEffect(() => {
    loadAll().catch(() => setLoading(false));
  }, [loadAll]);

  useEffect(() => {
    if (!isReadOnly || !ownerUid || !auctionId) return;
    const unsubscribe = subscribeToAuctionPurchases(ownerUid, auctionId, (rawPurchases) => {
      setPurchases(rawPurchases);
    });
    return unsubscribe;
  }, [isReadOnly, ownerUid, auctionId]);

  // Apre l'overlay di conferma; la rimozione effettiva avviene su conferma
  const handleRemovePurchase = (playerId) => {
    if (!user?.uid || !auctionId) return;
    setConfirmRemoveId(String(playerId));
  };

  const performRemovePurchase = async (playerId) => {
    if (!user?.uid || !auctionId) return;
    setSaving(true);
    try {
      await deletePurchase(user.uid, auctionId, String(playerId));

      setPurchases((prev) => updatePurchaseListAfterDelete(prev, playerId));
      setMarketJump((prev) =>
        prev?.playerId && String(prev.playerId) === String(playerId)
          ? null
          : prev
      );
    } finally {
      setSaving(false);
    }
  };

  // Asta a listone: rimozione con recupero crediti indicato dall'utente.
  const performRemovePurchaseListone = async (playerId, creditsRecovered) => {
    if (!user?.uid || !auctionId) return;
    setSaving(true);
    try {
      await deletePurchaseWithRecovery(user.uid, auctionId, String(playerId), creditsRecovered);
      setPurchases((prev) =>
        prev.map((p) =>
          String(p.playerId) === String(playerId)
            ? { ...p, deleted: true, creditsRecovered: Math.max(0, Number(creditsRecovered) || 0) }
            : p
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEditedPurchase = async (purchase, values) => {
    if (!user?.uid || !auctionId || !purchase) return;

    setSaving(true);
    try {
      const saved = {
        playerId: String(purchase.playerId),
        playerName: purchase.playerName,
        playerTeam: purchase.playerTeam,
        role: purchase.role,
        teamName: values.teamName,
        credits: Number(values.credits),
        acquiredAt: purchase.acquiredAt || Date.now(),
        playerSnapshot: purchase.playerSnapshot,
      };

      await savePurchase(user.uid, auctionId, saved, auction);

      setPurchases((prev) =>
        updatePurchaseListAfterSave(prev, {
          ...purchase,
          ...saved,
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePurchase = async ({ player, teamName, credits, existingPurchase }) => {
    if (!user?.uid || !auctionId || !player || !teamName || !credits) return;

    setSaving(true);
    try {
      const savedPurchase = {
        id: String(player.id),
        playerId: String(player.id),
        playerName: player.name,
        playerTeam: player.team,
        role: player.role,
        teamName,
        credits: Number(credits),
        acquiredAt: existingPurchase?.acquiredAt || Date.now(),
        playerSnapshot: player,
      };

      // Asta a listone: il documento acquisto è uno per giocatore. Se lo si
      // ricompra dopo averlo venduto, il risultato netto di quella vendita
      // (incassato − pagato) va conservato in `listoneCarry`, altrimenti il
      // nuovo acquisto lo cancellerebbe dal budget (vedi buildListoneEconomy).
      if (isListone) {
        const previous = (purchases || []).find((p) => String(p.playerId) === String(player.id));
        const previousCarry = Number.isFinite(Number(previous?.listoneCarry)) ? Number(previous.listoneCarry) : 0;
        if (previous?.deleted && Number.isFinite(Number(previous.creditsRecovered))) {
          savedPurchase.listoneCarry = previousCarry + Number(previous.creditsRecovered) - Number(previous.credits || 0);
          savedPurchase.creditsRecovered = null;
          savedPurchase.acquiredAt = Date.now();
        } else if (previousCarry) {
          savedPurchase.listoneCarry = previousCarry;
        }
      }

      await savePurchase(user.uid, auctionId, savedPurchase);

      setPurchases((prev) => {
        const next = updatePurchaseListAfterSave(prev, savedPurchase);
        return next;
      });

      setMarketJump((prev) =>
        prev?.playerId && String(prev.playerId) === String(player.id)
          ? null
          : prev
      );
    } finally {
      setSaving(false);
    }
  };

  // ─── Asta a listone: "Carica rose" (accanto a "Crediti iniziali") ─────────
  // Un solo caricamento aggiorna il tab Lega e la tua rosa: crediti pagati
  // (costo d'acquisto) e, dalla rosa CSV, la quotazione attuale proposta
  // quando rimuovi il giocatore (vedi RemoveOverlay nel tab Squadra).
  const rosterFileRef = useRef(null);
  const [rosterImportFiles, setRosterImportFiles] = useState(null);

  const applyListoneRosterImport = async ({ league, upserts = [], removals = [] }) => {
    if (!user?.uid || !auctionId) return;
    if (league) writeLeagueRosters(auctionId, league);
    if (!upserts.length && !removals.length) return;
    setSaving(true);
    try {
      const saved = upserts.map(({ player, credits, currentValue }) => {
        const id = String(player.id);
        const previous = (purchases || []).find((p) => String(p.playerId) === id);
        const purchase = {
          id,
          playerId: id,
          playerName: player.name,
          playerTeam: player.team,
          role: player.role,
          teamName: myTeamName,
          credits: Number(credits) || 0,
          currentValue: currentValue === null || currentValue === undefined ? null : Number(currentValue),
          acquiredAt: previous && !previous.deleted ? previous.acquiredAt || Date.now() : Date.now(),
          playerSnapshot: player,
        };
        // Stessa regola di handlePurchase: se il giocatore era stato venduto,
        // il risultato di quella vendita resta nel budget (listoneCarry).
        const previousCarry = Number.isFinite(Number(previous?.listoneCarry)) ? Number(previous.listoneCarry) : 0;
        if (previous?.deleted && Number.isFinite(Number(previous.creditsRecovered))) {
          purchase.listoneCarry = previousCarry + Number(previous.creditsRecovered) - Number(previous.credits || 0);
          purchase.creditsRecovered = null;
        } else if (previousCarry) {
          purchase.listoneCarry = previousCarry;
        }
        return purchase;
      });
      await Promise.all([
        ...saved.map((p) => savePurchase(user.uid, auctionId, p)),
        // Tolti perché non più nella rosa del file: rientrano i crediti pagati
        // (nessuna plus/minusvalenza inventata).
        ...removals.map((p) => deletePurchaseWithRecovery(user.uid, auctionId, String(p.playerId), Number(p.credits || 0))),
      ]);
      setPurchases((prev) => {
        let next = prev;
        saved.forEach((p) => { next = updatePurchaseListAfterSave(next, p); });
        const removedIds = new Set(removals.map((p) => String(p.playerId)));
        return next.map((p) => (removedIds.has(String(p.playerId))
          ? { ...p, deleted: true, creditsRecovered: Number(p.credits || 0) }
          : p));
      });
    } finally {
      setSaving(false);
    }
  };

  // Asta a listone: correzione manuale dei crediti disponibili (tab Market).
  // Si salva la differenza dal valore calcolato, non il numero assoluto.
  const handleSetListoneCreditsAdjustment = useCallback((adjustment) => {
    if (!user?.uid || !auctionId || isReadOnly) return;
    const value = Number.isFinite(Number(adjustment)) ? Math.round(Number(adjustment)) : 0;
    setAuction((prev) => (prev ? { ...prev, listoneCreditsAdjustment: value } : prev));
    updateAuction(user.uid, auctionId, { listoneCreditsAdjustment: value }).catch(() => {});
  }, [user?.uid, auctionId, isReadOnly]);

  const lastSentPlayersCountRef = useRef(null);
  const handleAuctionPlayersCountChange = useCallback(
    async (count) => {
      if (!user?.uid || !auctionId) return;

      const nextCount = Number(count) || 0;
      if (lastSentPlayersCountRef.current === nextCount) return;
      lastSentPlayersCountRef.current = nextCount;

      setAuction((prev) =>
        prev && Number(prev.playersBoughtCount || 0) !== nextCount
          ? { ...prev, playersBoughtCount: nextCount }
          : prev
      );

      await updateAuction(user.uid, auctionId, {
        playersBoughtCount: nextCount,
      });
    },
    [auctionId, user?.uid]
  );

  const handleChangePlayerCategory = useCallback(
    async (playerId, category) => {
      if (!user?.uid || !auctionId || !playerId) return;

      const key = String(playerId);
      const previousValue = String(playerCategoriesById?.[key] || 'none');
      const normalized = normalizePlayerCategory(category);

      setSavingPlayerCategoryId(key);

      try {
        await saveAuctionPlayerCategory(user.uid, auctionId, key, normalized);

        setPlayerCategoriesById((prev) => {
          const next = { ...prev };
          if (normalized === 'none') {
            delete next[key];
          } else {
            next[key] = normalized;
          }
          return next;
        });
      } catch (error) {
        setPlayerCategoriesById((prev) => ({
          ...prev,
          [key]: previousValue,
        }));
        throw error;
      } finally {
        setSavingPlayerCategoryId(null);
      }
    },
    [auctionId, playerCategoriesById, user?.uid]
  );

  const handleChangePlayerTier = useCallback(
    async (playerId, tier) => {
      if (!user?.uid || !auctionId || !playerId) return;

      const key = String(playerId);
      const previousValue = Number(playerTiersById?.[key] || 0);
      const normalized = normalizePlayerTier(tier);

      setSavingPlayerTierId(key);

      try {
        await saveAuctionPlayerTier(user.uid, auctionId, key, normalized);

        setPlayerTiersById((prev) => {
          const next = { ...prev };
          if (normalized === 0) {
            delete next[key];
          } else {
            next[key] = normalized;
          }
          return next;
        });
      } catch (error) {
        setPlayerTiersById((prev) => ({
          ...prev,
          [key]: previousValue,
        }));
        throw error;
      } finally {
        setSavingPlayerTierId(null);
      }
    },
    [auctionId, playerTiersById, user?.uid]
  );

  const handleChangePlayerMaxSpend = useCallback(
    async (playerId, credits) => {
      if (!user?.uid || !auctionId || !playerId) return;

      const key = String(playerId);
      const previousValue = Number(playerMaxSpendById?.[key] || 0);
      const normalized = normalizePlayerMaxSpend(credits);

      setSavingPlayerMaxSpendId(key);

      try {
        await saveAuctionPlayerMaxSpend(user.uid, auctionId, key, normalized);

        setPlayerMaxSpendById((prev) => {
          const next = { ...prev };
          if (normalized === 0) {
            delete next[key];
          } else {
            next[key] = normalized;
          }
          return next;
        });
      } catch (error) {
        setPlayerMaxSpendById((prev) => ({
          ...prev,
          [key]: previousValue,
        }));
        throw error;
      } finally {
        setSavingPlayerMaxSpendId(null);
      }
    },
    [auctionId, playerMaxSpendById, user?.uid]
  );

  const handleOpenMarketFromUnavailable = (playerId) => {
    setMarketJump({
      playerId: String(playerId),
      winningTeam: myTeamName,
      token: Date.now(),
    });
    setTab('market');
  };

  const handleSavePlayerValuation = useCallback(
    async (purchaseId, valuation) => {
      if (!user?.uid || !auctionId || !purchaseId) return;

      await savePurchaseValuation(user.uid, auctionId, String(purchaseId), Number(valuation));

      setPurchases((prev) =>
        prev.map((purchase) =>
          String(purchase.playerId) === String(purchaseId)
            ? {
              ...purchase,
              valuation: Number(valuation),
              playerSnapshot: {
                ...(purchase.playerSnapshot || {}),
                value: Number(valuation),
              },
            }
            : purchase
        )
      );
    },
    [auctionId, user?.uid]
  );

  // Copia etichette/fasce/note di una strategia salvata dentro questa asta.
  // importStrategyIntoAuction prima svuota l'asta da ogni dato di strategia
  // precedente, poi applica solo quelli della nuova: qui rispecchiamo lo stesso
  // in locale sostituendo (non unendo) gli stati. Da qui in poi le due cose
  // sono scollegate: modifiche fatte in asta dopo l'import non tornano mai
  // nella strategia.
  const handleImportStrategy = useCallback(
    async (strategy) => {
      if (!user?.uid || !auctionId || !strategy) return;

      const { categories, tiers, notes, maxSpends, backupGoalkeepers } = await importStrategyIntoAuction(user.uid, auctionId, strategy, auction);

      setPlayerCategoriesById(categories || {});
      setPlayerTiersById(tiers || {});
      setPlayerNotesById(notes || {});
      setPlayerMaxSpendById(maxSpends || {});
      setPlayerBackupGoalkeepersById(backupGoalkeepers || {});
    },
    [auctionId, user?.uid, auction]
  );

  // "Reset strategia": svuota l'asta da tutte le etichette/fasce/note/tetti di
  // spesa/portieri di riserva importati da una strategia. Non tocca gli
  // acquisti né la strategia sorgente.
  const handleResetStrategy = useCallback(async () => {
    if (!user?.uid || !auctionId || resettingStrategy) return;
    setResettingStrategy(true);
    try {
      await clearAuctionStrategy(user.uid, auctionId);
      setPlayerCategoriesById({});
      setPlayerTiersById({});
      setPlayerNotesById({});
      setPlayerMaxSpendById({});
      setPlayerBackupGoalkeepersById({});
      setShowResetStrategy(false);
    } finally {
      setResettingStrategy(false);
    }
  }, [auctionId, user?.uid, resettingStrategy]);

  const hasStrategyData =
    Object.keys(playerCategoriesById).length > 0 ||
    Object.keys(playerTiersById).length > 0 ||
    Object.keys(playerNotesById).length > 0 ||
    Object.keys(playerMaxSpendById).length > 0 ||
    Object.keys(playerBackupGoalkeepersById).length > 0;

  const handleSavePlayerNote = useCallback(
    async (playerId, notes) => {
      if (!user?.uid || !auctionId || !playerId) return;

      const key = String(playerId);
      const normalized = String(notes || '').trim();

      await saveAuctionPlayerNote(user.uid, auctionId, key, normalized);

      setPlayerNotesById((prev) => {
        const next = { ...prev };
        if (normalized) {
          next[key] = normalized;
        } else {
          delete next[key];
        }
        return next;
      });
    },
    [auctionId, user?.uid]
  );

  if (loading || !auction) {
    return (
      <main className="container auction-detail-page">
        <TopBar />
        <div className="auction-detail-empty glass">
          {loading ? 'Caricamento asta...' : 'Asta non trovata.'}
        </div>
      </main>
    );
  }

  return (
    <main className="container auction-detail-page">
      <TopBar />

      <section className="auction-detail-hero glass">
        <button
          type="button"
          className="auction-hero-toggle"
          onClick={() => setHeroCollapsed((prev) => !prev)}
          aria-expanded={!heroCollapsed}
        >
          {heroCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          {heroCollapsed ? 'Mostra dettagli asta' : 'Nascondi dettagli asta'}
        </button>

        <div className={`auction-detail-hero__content${heroCollapsed ? ' is-collapsed' : ''}`}>
          <div>
            <button className="back-btn" onClick={() => navigate('/aste')} type="button">
              <ArrowLeft size={16} />
              Torna alle aste
            </button>

            <Tooltip
              as="div"
              className={`auction-detail-hero__eyebrow${isReadOnly ? ' auction-detail-hero__eyebrow--readonly' : ''}`}
              content={isReadOnly ? 'Stai osservando questa asta: non puoi effettuare modifiche' : undefined}
            >
              {isReadOnly ? <span className="auction-detail-hero__live-dot" aria-hidden="true" /> : <Lock size={14} />}
              {isReadOnly ? <Eye size={14} /> : null}
              {isReadOnly
                ? 'Sola lettura'
                : `${auction.myTeamName} • ${isListone ? 'Listone' : 'Asta'} ${rolesMode === 'mantra' ? 'Mantra' : 'Classic'}`}
            </Tooltip>

            <h1>{auction.name}</h1>
          </div>

          <div className="auction-detail-hero__summary">
            {!isReadOnly && !isListone && (
              <Tooltip
                as="button"
                type="button"
                className="ad-import-strategy-btn"
                onClick={() => setShowImportStrategy(true)}
                content="Importa etichette, fasce e note da una strategia salvata (sostituisce quella già importata)"
              >
                <Download size={14} />
                Importa strategia
              </Tooltip>
            )}
            {!isReadOnly && !isListone && hasStrategyData && (
              <Tooltip
                as="button"
                type="button"
                className="ad-reset-strategy-btn"
                onClick={() => setShowResetStrategy(true)}
                content="Svuota l'asta da etichette, fasce, note e tetti di spesa importati"
              >
                <Eraser size={14} />
                Reset strategia
              </Tooltip>
            )}
            {!isListone && <Metric label="Partecipanti" value={auction.participantsCount} />}
            {isListone && !isReadOnly && (
              <>
                <Tooltip
                  as="button"
                  type="button"
                  className="ad-roster-upload-btn"
                  onClick={() => rosterFileRef.current?.click()}
                  content="Rosa CSV della tua squadra o export 'Esporta rose' della lega: aggiorna il tab Lega e la tua rosa (crediti pagati e quotazione attuale)"
                >
                  <Upload size={14} />
                  Carica rose (Excel o CSV)
                </Tooltip>
                <input
                  ref={rosterFileRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  multiple
                  hidden
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    e.target.value = '';
                    if (files.length) setRosterImportFiles(files);
                  }}
                />
              </>
            )}
            <Metric label="Crediti iniziali" value={auction.credits} />
            <Metric
              label="Crediti residui"
              value={isListone ? listoneEconomy.available : (teamEconomy?.[myTeamName]?.remaining ?? auction.credits)}
            />
            <Metric label="Modalità" value={`${isListone ? 'Listone ' : ''}${rolesMode === 'mantra' ? 'Mantra' : 'Classic'}`} />
          </div>
        </div>
      </section>

      {isListone && (
        <section className="auction-tabs glass">
          <button className={tab === 'squadra' ? 'active' : ''} onClick={() => setTab('squadra')} type="button">
            Squadra
          </button>
          <button className={tab === 'formazione' ? 'active' : ''} onClick={() => setTab('formazione')} type="button">
            Formazione
          </button>
          {rolesMode === 'mantra' && (
            <button className={tab === 'analisi' ? 'active' : ''} onClick={() => setTab('analisi')} type="button">
              Analisi
            </button>
          )}
          <button className={tab === 'lega' ? 'active' : ''} onClick={() => setTab('lega')} type="button">
            Lega
          </button>
        </section>
      )}
      {!isListone && rolesMode === 'mantra' && (
        <section className="auction-tabs-mantra glass">
          <button className={tab === 'market' ? 'active' : ''} onClick={() => setTab('market')} type="button">
            Market
          </button>
          <button className={tab === 'squadre' ? 'active' : ''} onClick={() => setTab('squadre')} type="button">
            Squadre
          </button>
          <button className={tab === 'svincolati' ? 'active' : ''} onClick={() => setTab('svincolati')} type="button">
            Svincolati
          </button>
          <button className={tab === 'formazione' ? 'active' : ''} onClick={() => setTab('formazione')} type="button">
            Formazione
          </button>
          <button className={tab === 'mantra' ? 'active' : ''} onClick={() => setTab('mantra')} type="button">
            Mantra
          </button>
        </section>
      )}
      {!isListone && rolesMode === 'classic' && (
        <section className="auction-tabs glass">
          <button className={tab === 'market' ? 'active' : ''} onClick={() => setTab('market')} type="button">
            Market
          </button>
          <button className={tab === 'squadre' ? 'active' : ''} onClick={() => setTab('squadre')} type="button">
            Squadre
          </button>
          <button className={tab === 'svincolati' ? 'active' : ''} onClick={() => setTab('svincolati')} type="button">
            Svincolati
          </button>
          <button className={tab === 'formazione' ? 'active' : ''} onClick={() => setTab('formazione')} type="button">
            Formazione
          </button>
        </section>
      )}


      {isListone && (
        <section style={{ display: tab === 'squadra' ? 'block' : 'none' }}>
          <AuctionListoneSquadraTab
            auction={auction}
            players={effectivePlayers}
            purchases={effectivePurchases}
            rolesMode={rolesMode}
            rosterLimits={rosterLimits}
            rosterMinLimits={rosterMinLimits}
            myTeamName={myTeamName}
            playerCategoriesById={playerCategoriesById}
            onPurchase={handlePurchase}
            playProbabilityById={playProbabilityById}
            onRemovePurchaseListone={performRemovePurchaseListone}
            onOpenPlayerDetail={openPlayerDetailNewTab}
            onAuctionPlayersCountChange={handleAuctionPlayersCountChange}
            onSetAvailableCredits={handleSetListoneCreditsAdjustment}
            readOnly={isReadOnly}
            saving={saving}
          />
        </section>
      )}

      {!isListone && (
      <>
      <section style={{ display: tab === 'market' ? 'block' : 'none' }}>
        <AuctionMarketTab
          auction={auction}
          players={effectivePlayers}
          purchases={effectivePurchases}
          teamNames={teamNames}
          teamEconomy={teamEconomy}
          rosterLimits={rosterLimits}
          rosterMinLimits={rosterMinLimits}
          rolesMode={rolesMode}
          marketPmaByPlayerId={marketPmaByPlayerId}
          playerCategoriesById={playerCategoriesById}
          playerTiersById={playerTiersById}
          playerMaxSpendById={playerMaxSpendById}
          initialSelectedPlayerId={marketJump?.playerId || null}
          initialWinningTeam={marketJump?.winningTeam || ''}
          jumpToken={marketJump?.token || 0}
          onPurchase={handlePurchase}
          onRemovePurchase={handleRemovePurchase}
          onSaveEditedPurchase={handleSaveEditedPurchase}
          readOnly={isReadOnly}
          onOpenPlayerDetail={openPlayerDetailNewTab}
          onOpenTeamOverlay={() => { }}
          onAuctionPlayersCountChange={handleAuctionPlayersCountChange}
          playerNotesById={playerNotesById}
          playerBackupGoalkeepersById={playerBackupGoalkeepersById}
          playProbabilityById={playProbabilityById}
          customCategoryLabel={customCategoryLabel}
          onSelectionChange={handleMarketSelectionChange}
        />
      </section>

      <section style={{ display: tab === 'squadre' ? 'block' : 'none' }}>
        <AuctionTeamsTab
          auction={auction}
          teamNames={teamNames}
          rosterByTeam={rosterByTeam}
          teamEconomy={teamEconomy}
          rosterLimits={rosterLimits}
          players={effectivePlayers}
          myTeamName={myTeamName}
          myRosterPurchases={myRosterPurchases}
          allPurchases={effectivePurchases}
          rolesMode={rolesMode}
          onRemovePurchase={handleRemovePurchase}
          onRestorePurchase={performRemovePurchase}
          onPurchase={handlePurchase}
          onOpenPlayerDetail={openPlayerDetailNewTab}
          readOnly={isReadOnly}
          playProbabilityById={playProbabilityById}
          marketSelection={marketSelection}
        />
      </section>

      <section style={{ display: tab === 'svincolati' ? 'block' : 'none' }}>
        <AuctionUnavailableTab
          auction={auction}
          players={effectivePlayers}
          purchases={effectivePurchases}
          marketPmaByPlayerId={marketPmaByPlayerId}
          rolesMode={rolesMode}
          playerCategoriesById={playerCategoriesById}
          savingPlayerCategoryId={savingPlayerCategoryId}
          onChangePlayerCategory={handleChangePlayerCategory}
          playerTiersById={playerTiersById}
          savingPlayerTierId={savingPlayerTierId}
          onChangePlayerTier={handleChangePlayerTier}
          playerMaxSpendById={playerMaxSpendById}
          savingPlayerMaxSpendId={savingPlayerMaxSpendId}
          onChangePlayerMaxSpend={handleChangePlayerMaxSpend}
          onOpenPlayerDetail={openPlayerDetailNewTab}
          onOpenInMarket={handleOpenMarketFromUnavailable}
          playerNotesById={playerNotesById}
          onSavePlayerNote={handleSavePlayerNote}
          playProbabilityById={playProbabilityById}
          readOnly={isReadOnly}
          customCategoryLabel={customCategoryLabel}
          onRenameCustomCategory={renameCustomCategoryLabel}
        />
      </section>
      </>
      )}

      <section style={{ display: tab === 'formazione' ? 'block' : 'none' }}>
        <AuctionFormationTab
          auction={auction}
          rolesMode={rolesMode}
          rosterByTeam={rosterByTeam}
          teamNames={teamNames}
          players={effectivePlayers}
          selectedTeamName={formationTeamName}
          onSelectedTeamNameChange={setFormationTeamName}
          myRosterPurchases={myRosterPurchases}
          initialFormation={auction?.formationPreset || (rolesMode === 'mantra' ? '343' : '4-3-3')}
          saving={saving}
          onSavePlayerValuation={handleSavePlayerValuation}
          playerCategoriesById={playerCategoriesById}
          playProbabilityById={playProbabilityById}
        />
      </section>

      {tab === 'mantra' && (
        <AuctionMantraManageTab
          auction={auction}
          roster={myRosterPurchases}
          players={effectivePlayers}
          purchases={effectivePurchases}
          rolesMode={rolesMode}
          onOpenPlayerDetail={openPlayerDetailNewTab}
          onSavePlayerValuation={handleSavePlayerValuation}
          readOnly={isReadOnly}
          playProbabilityById={playProbabilityById}
        />
      )}

      {isListone && tab === 'analisi' && rolesMode === 'mantra' && (
        <AuctionMantraManageTab
          auction={auction}
          roster={myRosterPurchases}
          players={effectivePlayers}
          purchases={effectivePurchases}
          rolesMode={rolesMode}
          onOpenPlayerDetail={openPlayerDetailNewTab}
          onSavePlayerValuation={handleSavePlayerValuation}
          readOnly={isReadOnly}
          playProbabilityById={playProbabilityById}
        />
      )}

      {isListone && rosterImportFiles && (
        <ListoneRosterImport
          files={rosterImportFiles}
          auctionId={auctionId}
          players={effectivePlayers}
          myRoster={myRosterPurchases}
          myTeamName={myTeamName}
          onClose={() => setRosterImportFiles(null)}
          onApply={applyListoneRosterImport}
        />
      )}

      {isListone && tab === 'lega' && (
        <AuctionListoneLegaTab
          auction={auction}
          roster={myRosterPurchases}
          players={effectivePlayers}
          rolesMode={rolesMode}
          myTeamName={myTeamName}
        />
      )}

      {confirmRemoveId !== null && (() => {
        const pending = (purchases || []).find(
          (p) => String(p.playerId) === String(confirmRemoveId) && !p.deleted
        );
        const pName = pending?.playerSnapshot?.name || pending?.playerName || 'questo giocatore';

        return (
          <div
            className="overlay-backdrop"
            onClick={() => { if (!saving) setConfirmRemoveId(null); }}
          >
            <div
              className="overlay-panel glass confirm-panel"
              onClick={(e) => e.stopPropagation()}
              role="alertdialog"
              aria-modal="true"
            >
              <div className="confirm-panel__head">
                <div className="confirm-panel__icon"><Trash2 size={20} /></div>
                <div>
                  <h3>Rimuovere l'acquisto?</h3>
                  <p>
                    Rendi <strong>{pName}</strong> nuovamente acquistabile.
                  </p>
                </div>
              </div>

              <div className="confirm-panel__actions">
                <button
                  type="button"
                  className="confirm-btn confirm-btn--ghost"
                  onClick={() => setConfirmRemoveId(null)}
                  disabled={saving}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className="confirm-btn confirm-btn--danger"
                  disabled={saving}
                  onClick={async () => {
                    const id = confirmRemoveId;
                    setConfirmRemoveId(null);
                    await performRemovePurchase(id);
                  }}
                >
                  {saving ? 'Rimozione…' : 'Rimuovi'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <StrategyManage
        open={showImportStrategy}
        uid={user?.uid}
        onClose={() => setShowImportStrategy(false)}
        onImport={handleImportStrategy}
      />

      {showResetStrategy && (
        <div
          className="overlay-backdrop"
          onClick={() => { if (!resettingStrategy) setShowResetStrategy(false); }}
        >
          <div
            className="overlay-panel glass confirm-panel"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
          >
            <div className="confirm-panel__head">
              <div className="confirm-panel__icon"><Eraser size={20} /></div>
              <div>
                <h3>Reset strategia?</h3>
                <p>
                  Rimuove da questa asta <strong>tutte</strong> le etichette, le fasce,
                  le note, i tetti di spesa e i portieri di riserva importati.
                  Gli acquisti e la strategia salvata non vengono toccati.
                </p>
              </div>
            </div>

            <div className="confirm-panel__actions">
              <button
                type="button"
                className="confirm-btn confirm-btn--ghost"
                onClick={() => setShowResetStrategy(false)}
                disabled={resettingStrategy}
              >
                Annulla
              </button>
              <button
                type="button"
                className="confirm-btn confirm-btn--danger"
                disabled={resettingStrategy}
                onClick={handleResetStrategy}
              >
                {resettingStrategy ? 'Reset…' : 'Reset strategia'}
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="auctions-footer">
        <span>FantApp! - All rights reserved - Sorgenti giocatori </span>
        <a href="https://www.fantamaster.it/" target="_blank" rel="noreferrer">
          FantaMaster
        </a>
      </footer>
    </main>
  );
}